import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { authApi } from '@/lib/api';
import { disconnectSocket } from '@/lib/socket';
import { onSessionEnded } from '@/lib/session';

interface AuthUser {
  id: string;
  role: string;
  fullName?: string;
  email?: string;
  institutionId?: string;
}

interface AuthState {
  user: AuthUser | null;
  accessToken: string | null;
  isAuthenticated: boolean;
  setAuth: (token: string, user: AuthUser) => void;
  logout: () => Promise<void>;
  /* Local sign-out with no network call — for a session that has already
     expired, where the token that would authorise /auth/logout is the one that
     just ran out. */
  clearSession: () => void;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      user: null,
      accessToken: null,
      isAuthenticated: false,

      setAuth: (token, user) => {
        localStorage.setItem('access_token', token);
        set({ user, accessToken: token, isAuthenticated: true });
      },

      logout: async () => {
        try {
          await authApi.logout();
        } catch {
          // ignore
        }
        localStorage.removeItem('access_token');
        disconnectSocket();
        set({ user: null, accessToken: null, isAuthenticated: false });
      },

      clearSession: () => {
        localStorage.removeItem('access_token');
        disconnectSocket();
        set({ user: null, accessToken: null, isAuthenticated: false });
      },
    }),
    {
      name: 'safepass-auth',
      partialize: (state) => ({ user: state.user, accessToken: state.accessToken, isAuthenticated: state.isAuthenticated }),
    }
  )
);

/*
  Flipping `isAuthenticated` is the whole redirect: Layout, GateLayout and
  ParentLayout each already send an unauthenticated visitor to THEIR login
  screen, so a staff member, a gate officer and a parent each land where they
  belong without this having to know which of them just expired.
*/
onSessionEnded(() => {
  useAuthStore.getState().clearSession();
});
